import React, { useCallback, useEffect, useMemo } from 'react';
import i18n from 'i18next';
import { useCookies } from 'react-cookie';
import { createPortal } from 'react-dom';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { Outlet, useLoaderData, useLocation, useSearchParams } from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { en } from '@/features/translations/en';
import { CorporationCode, getCorpTranslations } from '@/features/translations/helpers';
import { zh_hk } from '@/features/translations/zh-hk';

import store from '@/store';
import { setGoogleClickId, setPersonaId } from '@/store/bookingSlice';

import Navbar from '../components/elements/navbar';
import DebugBar from '@/components/debug-bar';
import ContactCard from '@/components/elements/contact-card';

import LocationSettings from '../features/rapidstor/LocationSettings';
import { dispatchEvent } from '@/features/rapidstor/PostMessageEvents';
import { AnalyticsContextProvider } from '@/features/rapidstor/providers/AnalyticsProvider';
import { JourneyContextProvider } from '@/features/rapidstor/providers/JourneyProvider';

export default function Root() {
    const location = useLoaderData() as Location;
    const [gclidCookie] = useCookies(['gclid']);
    const [agentDetailsCookie] = useCookies(['agentDetails']);
    const routerLocation = useLocation();
    const [personaCookie, setCookie] = useCookies(['persona']);
    const [searchParams] = useSearchParams();

    const personaId = searchParams.get('personaId') || window.rapidstor_settings.rapidstor_options.persona || personaCookie.persona || undefined;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const deepMerge = useCallback((target: any, source: any) => {
        for (const key in source) {
            if (source[key] instanceof Object) {
                if (!target[key]) Object.assign(target, { [key]: {} });
                deepMerge(target[key], source[key]);
            } else {
                Object.assign(target, { [key]: source[key] });
            }
        }
        return target;
    }, []);

    const resources = useMemo(() => ({
        en: deepMerge(en, getCorpTranslations(location.corpCode as CorporationCode)),
        'zh-HK': deepMerge(zh_hk, getCorpTranslations(location.corpCode as CorporationCode)),
    }), [location.corpCode, deepMerge]);

    useMemo(() => {
        i18n.use(initReactI18next).init({
            lng: window.rapidstor_settings.rapidstor_options.language || navigator.language,
            resources: resources,
            fallbackLng: 'en',
            interpolation: {
                escapeValue: false,
            },
        });
    }, [resources]);

    /**
     * Injects the site theme based on the location.
     */
    useEffect(() => {
        const personalisation = new LocationSettings(location);
        personalisation.injectSiteTheme();
    }, [location]);

    /**
     * Sets the Google Click ID in the store if it exists
     */
    useEffect(() => {
        if (gclidCookie) {
            store.dispatch(setGoogleClickId(`${gclidCookie.gclid}`));
        }
    }, [gclidCookie]);

    /**
     * Sets the personaId in the store if it exists
     */
    useEffect(() => {
        if (personaId) {
            setCookie(
                'persona', 
                personaId, 
                { 
                    path: '/', 
                    maxAge: 60 * 60 * 24 
                }
            );
            store.dispatch(setPersonaId(personaId));
        }
    }, [personaId, setCookie]);

    /**
     * Dispatches the location changed event to the parent window
     */
    useEffect(() => {
        dispatchEvent({
            type: 'location-changed',
            context: {
                pathname: routerLocation.pathname,
                hash: routerLocation.hash,
            },
        });
    }, [routerLocation]);

    /**
     * Handles the agent details cookies for the UKStorage Callcenter Dashboard.
     * Pushes data through into the externalData object in the reservation payloads.
     */
    useEffect(() => {
        if (agentDetailsCookie && agentDetailsCookie.agentDetails) {
            window.rapidstor_settings.app_config.journey.agentDetails = {
                agentName: agentDetailsCookie.agentDetails.agentName,
                agentComment: agentDetailsCookie.agentDetails.comments,
            };
        }
    }, [agentDetailsCookie]);

    return (
        <>
            <AnalyticsContextProvider location={location}>
                <I18nextProvider i18n={i18n}>
                    <JourneyContextProvider location={location}>
                        <>
                            <Navbar location={location} />

                            <Outlet />

                            {createPortal(<ContactCard location={location} />, document.body)}
                        </>
                    </JourneyContextProvider>
                </I18nextProvider>
            </AnalyticsContextProvider>
            {window.rapidstor_settings.app_config.journey.configSelector && <DebugBar />}
        </>
    );
}
